"use client";

import { useRef } from "react";
import { gsap, ScrollTrigger, useGSAP } from "@/lib/gsap";
import { blurReveal, dotToPill, fadeUp, reduceMotion } from "@/lib/motion";
import { INTRO_COMPLETE_EVENT, INTRO_HOLD, INTRO_TOTAL } from "@/lib/lenis-context";
import { TextReveal } from "@/components/motion/TextReveal";
import { Button } from "@/components/ui/Button";
import { LOGO_MASK, Logo } from "@/components/ui/Logo";
import { Check } from "@/components/ui/icons";
import { QuikitLogoAnimation } from "@/components/hero/QuikitLogoAnimation";
import { HERO, LINKS } from "@/content/quikit";
import { DarkPanel } from "./DarkPanel";

const HEADLINE_GRADIENT = "linear-gradient(90deg, #ffffff 0%, #f5f5f5 34%, #8e8e93 100%)";

/**
 * Opening shot. Timeline (seconds):
 *  0.00  wordmark letters rise through their mask, centred on white
 *  0.34  the dark stage slides up from below the fold (power3.out)
 *  0.50  wordmark exits upward; 0.74 the nav wordmark + links drop into the bar
 *  0.70  announcement pill; 0.74 headline words de-blur one after another
 *  0.95  sage light blooms, grid fades in; 1.28 supporting copy
 *  1.45  CTAs pop as dots and stretch into pills; trust checks settle
 *  1.62  workspace floats up (scale 1.05 → 1), then its panels cascade in
 */
export function Hero() {
  const root = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      const el = root.current;
      if (!el) return;
      const html = document.documentElement;
      const q = (s: string) => el.querySelector<HTMLElement>(s);
      const qa = (s: string) => Array.from(el.querySelectorAll<HTMLElement>(s));

      const introLogo = q("[data-intro-logo]");
      const introLetters = introLogo ? Array.from(introLogo.querySelectorAll("[data-logo-letter]")) : [];
      const panel = q("[data-dark-panel]");
      const glow = q("[data-panel-glow]");
      const glowWrap = q("[data-panel-glow-wrap]");
      const grid = q("[data-panel-grid]");
      const pill = q("[data-hero-pill]");
      const words = qa("#hero-title [data-tr-unit]");
      const sub = q("[data-hero-sub]");
      const ctas = qa("[data-hero-cta] [data-dot]");
      const micro = qa("[data-hero-micro]");
      const copy = q("[data-hero-copy]");
      const mockParallax = q("[data-hero-mock-parallax]");
      const mock = q("[data-hero-mock]");
      const navLogo = document.querySelector<HTMLElement>("[data-nav-logo]");
      const navLetters = navLogo ? Array.from(navLogo.querySelectorAll("[data-logo-letter]")) : [];
      const navItems = Array.from(document.querySelectorAll<HTMLElement>("[data-nav-item]")).filter(
        (n) => n.offsetParent !== null,
      );

      const finishIntro = () => {
        html.dataset.introDone = "1";
        window.dispatchEvent(new Event(INTRO_COMPLETE_EVENT));
      };

      if (reduceMotion() || !panel) {
        html.classList.remove("intro");
        if (introLogo) introLogo.style.display = "none";
        finishIntro();
        return;
      }

      /* The entrance belongs to the first load of the page only. `introDone` is set by the boot
       * script on any other first load and by the intro itself once it has played, so it is
       * already there when the visitor comes back to the homepage through client navigation
       * (Back, the logo, a nav link). Replaying it then held a blank wordmark screen for three
       * seconds over the page they had just left. Not a class or module flag: StrictMode runs
       * this effect twice on first load, and both runs must still play the intro. */
      const playIntro = !html.dataset.introDone;

      if (playIntro) window.scrollTo(0, 0);

      /* ---------- Pre-intro state (applied before the CSS gate lifts) ---------- */
      const offscreen = window.innerHeight - panel.getBoundingClientRect().top + 24;
      gsap.set(introLogo, { autoAlpha: 1 });
      gsap.set(introLetters, { y: LOGO_MASK });
      gsap.set(panel, { autoAlpha: 1, y: offscreen });
      gsap.set(navLogo, { autoAlpha: 1 });
      gsap.set(navLetters, { y: LOGO_MASK });
      gsap.set(navItems, { autoAlpha: 0, y: -16 });
      gsap.set([glow, grid], { autoAlpha: 0 });
      gsap.set(mock, { autoAlpha: 0, y: 60, scale: 0.94 });
      html.classList.remove("intro");

      /* ---------- Entrance ---------- */
      const tl = gsap.timeline({ delay: 0.12, onComplete: () => ScrollTrigger.refresh() });

      // The wordmark rises, then rests for INTRO_HOLD seconds; everything after is offset by H.
      const H = INTRO_HOLD;
      tl.to(introLetters, { y: 0, duration: 0.85, ease: "premium", stagger: 0.05 }, 0)
        .to(panel, { y: 0, duration: 1.35, ease: "power3.out" }, 0.34 + H)
        .to(introLetters, { y: -LOGO_MASK, duration: 0.5, ease: "glide", stagger: 0.025 }, 0.5 + H)
        .set(introLogo, { display: "none" }, 1.2 + H)
        .to(navLetters, { y: 0, duration: 0.85, ease: "premium", stagger: 0.04 }, 0.74 + H)
        .to(navItems, { autoAlpha: 1, y: 0, duration: 0.9, ease: "silk", stagger: 0.04 }, 0.76 + H)
        .add(fadeUp(pill, { duration: 0.9 }), 0.68 + H)
        .add(blurReveal(words, { duration: 1.2, stagger: 0.075 }), 0.76 + H)
        .to(glow, { autoAlpha: 1, duration: 1.8, ease: "power2.out" }, 0.95 + H)
        .fromTo(glow, { scale: 0.8 }, { scale: 1, duration: 2.2, ease: "silk" }, 0.95 + H)
        .to(grid, { autoAlpha: 1, duration: 1.6, ease: "power2.out" }, 1.05 + H)
        .add(fadeUp(sub, { duration: 1 }), 1.28 + H)
        .call(finishIntro, undefined, 1.5 + H);

      ctas.forEach((cta, i) => dotToPill(cta, tl, 1.45 + H + i * 0.12));
      tl.add(fadeUp(micro, { duration: 0.8, stagger: 0.08 }), 1.7 + H);

      tl.to(mock, { autoAlpha: 1, y: 0, scale: 1, duration: 1.6, ease: "silk" }, 1.62 + H);

      // Fit the whole choreography into INTRO_TOTAL seconds (the 0.12s start delay included),
      // keeping every step's relative timing intact.
      tl.timeScale(tl.duration() / (INTRO_TOTAL - 0.12));

      // Coming back: render the finished state before first paint, callbacks included (the
      // CTA clip is dropped, triggers re-measure), so the hero is simply there.
      if (!playIntro) tl.progress(1);

      // Dev-only handle for frame-by-frame review: window.__quikitIntro.pause().time(1.2)
      if (process.env.NODE_ENV !== "production") {
        (window as unknown as { __quikitIntro?: gsap.core.Timeline }).__quikitIntro = tl;
      }

      /* ---------- Exit: scroll-linked depth as the stage leaves ---------- */
      const mm = gsap.matchMedia();
      mm.add({ desktop: "(min-width: 768px)", mobile: "(max-width: 767px)" }, (ctx) => {
        const k = ctx.conditions?.desktop ? 1 : 0.55;
        gsap
          .timeline({ scrollTrigger: { trigger: el, start: "top top", end: "bottom top", scrub: true } })
          .to(panel, { scale: 0.955, ease: "none" }, 0)
          .to(copy, { y: 110 * k, opacity: 0.1, ease: "none" }, 0)
          .to(mockParallax, { y: -90 * k, ease: "none" }, 0)
          .to(glowWrap, { y: 150 * k, ease: "none" }, 0);
      });
    },
    { scope: root },
  );

  return (
    <div id="top" ref={root} data-hero="" className="relative pt-[calc(var(--nav-h)+10px)]">
      <div
        data-intro-logo=""
        aria-hidden="true"
        className="pointer-events-none invisible fixed inset-0 z-[1] grid place-items-center"
      >
        <Logo className="w-[min(58vw,340px)] text-ink" />
      </div>

      {/* Stacks above the intro wordmark so the rising stage slides over it */}
      <DarkPanel
        enter={false}
        exit={false}
        ariaLabelledby="hero-title"
        className="relative z-[2]"
        threads={[12, 24, 91]}
        /*
         * Full-height stage, except in portrait below `lg`. There the viewport is tall but
         * narrow and the app-icon row is sized by width, so 100svh left up to 43% of the
         * panel empty above it — worst on portrait tablets and large phones. Hugging the
         * content keeps the composition; `min-h` still sets the floor, so the stage reads
         * the same as it does everywhere else.
         */
        panelClassName="h-[calc(100svh-var(--nav-h)-10px-clamp(10px,2vw,30px))] min-h-[680px] max-h-[1060px] max-lg:portrait:h-auto"
        contentClassName="flex h-full flex-col items-center"
      >
        <div data-hero-copy="" className="flex w-full flex-col items-center px-5 pt-[clamp(40px,7vh,84px)] text-center">
          {/* A statement, not a link — nothing to click through to. */}
          <div
            data-hero-pill=""
            data-intro=""
            className="mb-6 inline-flex max-w-full items-center gap-2 rounded-full bg-white/[0.06] py-1 pl-1 pr-3 text-[11.5px] text-white/70 ring-1 ring-inset ring-white/10 md:text-[12px]"
          >
            <span className="rounded-full bg-mint px-2 py-0.5 text-[10px] font-semibold text-ink">{HERO.pill.tag}</span>
            <span className="truncate">{HERO.pill.text}</span>
          </div>
          <TextReveal
            as="h1"
            id="hero-title"
            mode="manual"
            text={HERO.title}
            gradient={HEADLINE_GRADIENT}
            className="max-w-[16em] text-[clamp(2.3rem,4.7vw,4.6rem)] font-[450] leading-[1.04] tracking-[-0.045em]"
          />
          <p
            data-hero-sub=""
            data-intro=""
            className="mt-5 max-w-[34rem] text-[14.5px] leading-[1.55] text-white/60 md:text-[15px]"
          >
            {HERO.sub}
          </p>
          <div data-hero-cta="" className="mt-7 flex flex-wrap justify-center gap-2">
            <Button variant="mint" dot href={LINKS.register} arrow="inline">
              Start 14-day free trial
            </Button>
            <Button variant="ghost" dot href={LINKS.demo}>
              Book a demo
            </Button>
          </div>
          <ul className="mt-5 flex flex-wrap justify-center gap-x-5 gap-y-1.5 text-[12px] text-white/50">
            {HERO.micro.map((m) => (
              <li key={m} data-hero-micro="" data-intro="" className="flex items-center gap-1.5">
                <Check className="size-3.5 text-mint" strokeWidth={2.2} />
                {m}
              </li>
            ))}
          </ul>
        </div>

        <div data-hero-mock-parallax="" className="relative flex w-full max-w-[1240px] flex-1 items-end justify-center px-[clamp(12px,3vw,40px)] pt-[clamp(20px,4vh,40px)]">
          <div data-hero-mock="" data-intro="" className="w-full origin-center">
            <QuikitLogoAnimation />
          </div>
        </div>
      </DarkPanel>
    </div>
  );
}
